import * as React from "react";
import { Button } from "../Button";
import { Callout } from "../Callout";
import { TextInput } from "../Form";
import { Modal } from "../Modal";
import type { ButtonColor } from "../Button/Button.types";
import { useT } from "../../../app/lib/i18n";
import * as s from "./ConfirmModal.css";

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  confirmColor?: ButtonColor;
  /**
   * When set, the user must type this exact string to enable the confirm button.
   * Use for irreversible destructive actions (e.g. hard deletes).
   */
  confirmPhrase?: string;
  loading?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * A styled confirmation dialog for destructive or irreversible actions.
 * Wraps the base Modal with a consistent layout: title, description, optional
 * phrase verification, and Cancel / Confirm action buttons.
 */
export function ConfirmModal({
  open,
  title,
  body,
  confirmLabel,
  confirmColor = "primary",
  confirmPhrase,
  loading = false,
  error = null,
  onCancel,
  onConfirm,
}: ConfirmModalProps) {
  const t = useT();
  const [phraseInput, setPhraseInput] = React.useState("");

  React.useEffect(() => {
    if (!open) setPhraseInput("");
  }, [open]);

  const handleClose = React.useCallback(() => {
    if (loading) return;
    onCancel();
  }, [loading, onCancel]);

  const confirmEnabled = !confirmPhrase || phraseInput === confirmPhrase;

  return (
    <Modal
      open={open}
      setOpen={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
      minWidth="420px"
      minHeight="auto"
      maxWidth="480px"
    >
      <Modal.Header>
        <h3 className={s.title}>{title}</h3>
      </Modal.Header>
      <Modal.Body>
        <div className={s.body}>
          <p className={s.text}>{body}</p>
          {confirmPhrase && (
            <TextInput
              label={t("shared.confirm.typeToConfirm", { phrase: confirmPhrase })}
              value={phraseInput}
              onChange={(e) => setPhraseInput(e.target.value)}
              autoComplete="off"
              disabled={loading}
            />
          )}
          {error && <Callout variant="error">{error}</Callout>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          color="neutral"
          variant="outline"
          size="md"
          disabled={loading}
          onClick={handleClose}
        >
          {t("shared.common.cancel")}
        </Button>
        <Button
          color={confirmColor}
          variant="solid"
          size="md"
          loading={loading}
          disabled={!confirmEnabled}
          onClick={() => void onConfirm()}
        >
          {confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
